/**
 * CrmKpiPrimitives.jsx
 *
 * Danışman CRM sayfaları arasında paylaşılan KPI kart primitifleri + gün/ay
 * gezinme kontrolü + tarih yardımcıları.
 *
 * Kaynak: bu bileşenler önceden `AdminCiroDashboard.jsx` içinde tanımlıydı.
 * Yeni "Danışman Ana Sayfası" (`AdminDanismanAnaSayfa.jsx`) da aynı
 * primitifleri kullandığı için buraya taşındı — davranış/stil DEĞİŞMEDİ,
 * sadece dosya konumu değişti. `AdminCiroDashboard.jsx` bu modülden import
 * eder, kendi kopyasını tutmaz.
 */
import { Skeleton } from '../ui/skeleton';
import { AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { trNowLocalDateInput, trNowLocalMonthInput } from '../../utils/trDateTime';

// ─── Tarih yardımcıları ─────────────────────────────────────────────────────

export function todayStr() {
  return trNowLocalDateInput();
}
export function currentMonthStr() {
  return trNowLocalMonthInput();
}
export function shiftDate(dateStr, deltaDays) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}
export function shiftMonth(monthStr, deltaMonths) {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + deltaMonths, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ─── KPI kart primitifleri ─────────────────────────────────────────────────

export function KpiCard({ icon: Icon, label, value, sub, subColor }) {
  return (
    <div
      className="bg-white p-4"
      style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon size={14} style={{ color: 'var(--crm-navy)' }} />}
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--crm-muted-2)' }}>
          {label}
        </span>
      </div>
      <p className="text-xl font-bold" style={{ color: 'var(--crm-navy-text)' }}>{value}</p>
      {sub && <p className="text-[11px] font-semibold mt-0.5" style={{ color: subColor || 'var(--crm-muted)' }}>{sub}</p>}
    </div>
  );
}

export function KpiSkeleton({ cols }) {
  return (
    <div className={`grid gap-3 ${cols === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
          <Skeleton className="h-3 w-2/3 mb-2" />
          <Skeleton className="h-6 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function KpiSection({ title, cols, cards, loading, error, onRetry, tr }) {
  if (!loading && !error && cards.length === 0) return null;
  return (
    <div className="mb-5">
      <h2 className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--crm-muted-2)' }}>
        {title}
      </h2>
      {loading && <KpiSkeleton cols={cols} />}
      {!loading && error && (
        <div
          className="p-3 rounded-xl flex items-center justify-between gap-3"
          style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}
        >
          <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
            <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
          </span>
          <button
            onClick={onRetry}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1"
            style={{ backgroundColor: 'var(--crm-danger)' }}
          >
            <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
          </button>
        </div>
      )}
      {!loading && !error && (
        <div className={`grid gap-3 ${cols === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
          {cards}
        </div>
      )}
    </div>
  );
}

// ─── Gün/Ay gezme kontrolü ──────────────────────────────────────────────────

export function NavControl({ value, onPrev, onNext, nextDisabled, tr }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onPrev}
        aria-label={tr('Önceki', 'Previous')}
        className="p-1.5 rounded-lg border"
        style={{ borderColor: 'var(--crm-card-border)', color: 'var(--crm-navy-text)' }}
      >
        <ChevronLeft size={14} />
      </button>
      <span className="text-xs font-bold min-w-[90px] text-center" style={{ color: 'var(--crm-navy-text)' }}>{value}</span>
      <button
        onClick={onNext}
        disabled={nextDisabled}
        aria-label={tr('Sonraki', 'Next')}
        aria-disabled={nextDisabled}
        className="p-1.5 rounded-lg border disabled:opacity-30"
        style={{ borderColor: 'var(--crm-card-border)', color: 'var(--crm-navy-text)' }}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}
